import { type ToolCall as AgUiToolCall } from '@ag-ui/client'
import type { CopilotPageElement } from '../pageContext/types'
import { isRecord, parseToolCallArgumentsForDisplay } from '../toolRegistry/parsing'
import { getPageElementLabel } from 'shared/copilot/components/ToolCall/pageElementDisplay'

/**
 * Extracts an optional `elementId` from `inspect_current_page` tool call arguments.
 * Returns undefined when the argument is missing, empty, or not a string.
 */
const getInspectCurrentPageElementId = (toolCall: AgUiToolCall): string | undefined => {
  const args = parseToolCallArgumentsForDisplay(toolCall.function.arguments)

  if (!isRecord(args) || typeof args.elementId !== 'string') {
    return undefined
  }

  const elementId = args.elementId.trim()
  return elementId.length > 0 ? elementId : undefined
}

/**
 * Builds thinking-signature title and description for `inspect_current_page` tool calls.
 * Uses the targeted element label when `elementId` is present, otherwise the whole page.
 */
export const getInspectCurrentPageThinkingSignature = (
  toolCall: AgUiToolCall,
  hasResult: boolean,
  elements: CopilotPageElement[] | undefined
): { title: string; description: string } => {
  const elementId = getInspectCurrentPageElementId(toolCall)

  if (elementId != null) {
    const elementName = getPageElementLabel(elementId, elements)

    return {
      title: hasResult ? `Inspected ${elementName}` : `Inspecting ${elementName}`,
      description: 'more details'
    }
  }

  return {
    title: hasResult ? 'Inspected current page' : 'Inspecting current page',
    description: 'more details'
  }
}
